import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";

/**
 * Website reachability pill — read-only.
 *
 * The verdict is produced automatically during MCA underwriting and stored on the
 * entity; there is no on-demand re-check, so this component only displays.
 *
 * Renders the three-way availability bucket rather than the raw status: the raw
 * status has five values meaningful to the pipeline but not to a user, while the
 * bucket answers the only question being asked — can a customer reach this site?
 *
 * Renders nothing when there is no verdict, so it can sit next to a website field
 * without leaving an empty artefact.
 */

const PRESENTATION = {
  REACHABLE_OR_UNKNOWN: {
    label: "Reachable",
    color: "#1E8E5A",
    background: "#EAF7F0",
    border: "#CBEBDA",
  },
  LIKELY_DOWN: {
    label: "Likely down",
    color: "#B26A00",
    background: "#FFF6E5",
    border: "#FFE3B0",
  },
  SAFELY_KNOWN_TOBE_DOWN: {
    label: "Unreachable",
    color: "#FF4D4D",
    background: "#FFF1F1",
    border: "#FFE0E0",
  },
};

const normalize = (value) => String(value || "").trim().toLowerCase()
  .replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0];

const formatCheckedAt = (checkedAt) => {
  if (!checkedAt) return null;
  const date = new Date(checkedAt);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
};

/**
 * Build the tooltip. `reason` is the probe's precise rationale ("HTTP 404",
 * "DNS does not resolve (NXDOMAIN)"); surfacing it means a user can tell a typo'd
 * domain from a genuinely dead one without asking anyone.
 */
const buildTooltip = (verdict) => {
  const lines = [];
  if (verdict.reason) lines.push(verdict.reason);
  if (verdict.redirectDomain) lines.push(`Redirects to ${verdict.redirectDomain}`);
  if (verdict.checkedDomain) lines.push(`Checked: ${verdict.checkedDomain}`);
  const when = formatCheckedAt(verdict.checkedAt);
  if (when) lines.push(`Last checked ${when}`);
  return lines.join(" · ") || "No further detail available.";
};

/**
 * @param {object} props.verdict  Stored websiteReachability from the entity.
 * @param {string} props.website  Current website — used to detect a stale verdict.
 */
function WebsiteReachabilityBadge({ verdict, website }) {
  if (!verdict) return null;

  // A verdict for a different domain describes the OLD website — showing it after
  // someone edits the field would be confidently wrong, so render nothing instead.
  if (verdict.checkedDomain && website && verdict.checkedDomain !== normalize(website)) {
    return null;
  }

  const style = PRESENTATION[verdict.availability];
  if (!style) return null;

  return (
    <Tooltip title={buildTooltip(verdict)} arrow>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          background: style.background,
          border: `1px solid ${style.border}`,
          borderRadius: "8px",
          padding: "2px 8px",
          verticalAlign: "middle",
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: style.color }} />
        <Typography sx={{ fontSize: "12px", fontWeight: 500, color: style.color, whiteSpace: "nowrap" }}>
          {style.label}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default WebsiteReachabilityBadge;
